import {
  DialogDescription,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogClose,
  DialogTitle,
  Button,
  Dialog,
  toast,
} from '@ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEmployees } from '@pages/employees/hooks';
import { useTranslation } from 'react-i18next';

interface RemoveEmployeeDialogProps {
  selectedEmployeeId: string;
  isOpen: boolean;
  onClose: ({ employeeRemoved }: { employeeRemoved: boolean }) => void;
}

export const RemoveEmployeeDialog = (props: RemoveEmployeeDialogProps) => {
  const { selectedEmployeeId, isOpen, onClose } = props;
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { mutateRemoveEmployee, isRemoveEmployeePending } = useEmployees({
    onRemoveSuccess: () => {
      onClose({ employeeRemoved: true });
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      toast.success(t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_SUCCESS'));
    },
    onRemoveError: () => {
      toast.error(t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_ERROR'));
    },
  });

  const handleRemoveEmployee = () => {
    mutateRemoveEmployee(selectedEmployeeId);
  };

  return (
    <Dialog onOpenChange={() => onClose({ employeeRemoved: false })} open={isOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_TITLE')}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_DESCRIPTION')}</DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={isRemoveEmployeePending} variant="tertiary" size="large" wide>
              {t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_CANCEL')}
            </Button>
          </DialogClose>
          <Button
            isLoading={isRemoveEmployeePending}
            disabled={isRemoveEmployeePending}
            onClick={handleRemoveEmployee}
            size="large"
            wide
          >
            {t('EMPLOYEES.REMOVE_EMPLOYEE_DIALOG_DELETE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
